import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION_NAME = 'vehicles';

// Get all vehicles
export const getVehicles = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const vehicles = [];
    querySnapshot.forEach((doc) => {
      vehicles.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return vehicles;
  } catch (error) {
    console.error('Error getting vehicles: ', error);
    throw error;
  }
};

export const getVehiclesByStatus = async (status) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const vehicles = [];
    querySnapshot.forEach((doc) => {
      vehicles.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return vehicles;
  } catch (error) {
    console.error('Error getting vehicles by status: ', error);
    throw error;
  }
};

export const subscribeToVehicles = (callback) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (querySnapshot) => {
    const vehicles = [];
    querySnapshot.forEach((doc) => {
      vehicles.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(vehicles);
  }, (error) => {
    console.error('Error in vehicles subscription: ', error);
    callback([]);
  });
};

export const addVehicle = async (vehicleData) => {
  try {
    // Validate required fields
    if (!vehicleData.registration_no || !vehicleData.make) {
      throw new Error('Registration number and make are required');
    }

    // Check if registration number already exists
    const existingVehicle = await getVehicleByRegistration(vehicleData.registration_no);
    if (existingVehicle) {
      throw new Error('A vehicle with this registration number already exists');
    }

    const vehicleDocData = {
      registration_no: vehicleData.registration_no.toUpperCase(),
      make: vehicleData.make,
      year: vehicleData.year || null,
      last_service_mileage: vehicleData.last_service_mileage || null,
      last_service_date: vehicleData.last_service_date || null,
      next_service_mileage: vehicleData.next_service_mileage || null,
      control_technique_date: vehicleData.control_technique_date || null,
      insurance_expiry_date: vehicleData.insurance_expiry_date || null,
      status: vehicleData.status || 'Active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), vehicleDocData);
    
    return {
      id: docRef.id,
      ...vehicleDocData
    };
  } catch (error) {
    console.error('Error adding vehicle: ', error);
    throw error;
  }
};

// Update a vehicle
export const updateVehicle = async (vehicleId, updateData) => {
  try {
    const vehicleRef = doc(db, COLLECTION_NAME, vehicleId);
    
    // If updating registration number, check for duplicates
    if (updateData.registration_no) {
      const existingVehicle = await getVehicleByRegistration(updateData.registration_no);
      if (existingVehicle && existingVehicle.id !== vehicleId) {
        throw new Error('A vehicle with this registration number already exists');
      }
      updateData.registration_no = updateData.registration_no.toUpperCase();
    }

    await updateDoc(vehicleRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating vehicle: ', error);
    throw error;
  }
};

// Delete a vehicle
export const deleteVehicle = async (vehicleId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, vehicleId));
  } catch (error) {
    console.error('Error deleting vehicle: ', error);
    throw error;
  }
};

// Get vehicle by ID
export const getVehicleById = async (vehicleId) => {
  try {
    const vehicleDoc = await getDoc(doc(db, COLLECTION_NAME, vehicleId));
    if (vehicleDoc.exists()) {
      return {
        id: vehicleDoc.id,
        ...vehicleDoc.data()
      };
    } else {
      throw new Error('Vehicle not found');
    }
  } catch (error) {
    console.error('Error getting vehicle: ', error);
    throw error;
  }
};

// Get vehicle by registration number
export const getVehicleByRegistration = async (registrationNo) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('registration_no', '==', registrationNo.toUpperCase())
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting vehicle by registration: ', error);
    throw error;
  }
};

// Get vehicles requiring service (based on next service mileage or date)
export const getVehiclesRequiringService = async () => {
  try {
    const vehicles = await getVehicles();
    const currentDate = new Date();
    
    return vehicles.filter(vehicle => {
      // Check if next service is due (you'd need current mileage for this)
      // For now, just check dates
      if (vehicle.control_technique_date) {
        const controlDate = new Date(vehicle.control_technique_date);
        const daysDiff = (controlDate - currentDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 30) { // Due within 30 days
          return true;
        }
      }
      
      if (vehicle.insurance_expiry_date) {
        const insuranceDate = new Date(vehicle.insurance_expiry_date);
        const daysDiff = (insuranceDate - currentDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 30) { // Expiring within 30 days
          return true;
        }
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error getting vehicles requiring service: ', error);
    throw error;
  }
};

// Update service information
export const updateServiceInfo = async (vehicleId, serviceData) => {
  try {
    const vehicleRef = doc(db, COLLECTION_NAME, vehicleId);
    await updateDoc(vehicleRef, {
      last_service_date: serviceData.last_service_date,
      last_service_mileage: serviceData.last_service_mileage,
      next_service_mileage: serviceData.next_service_mileage,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating service info: ', error);
    throw error;
  }
};

// Update inspection date
export const updateInspectionDate = async (vehicleId, controlDate) => {
  try {
    const vehicleRef = doc(db, COLLECTION_NAME, vehicleId);
    await updateDoc(vehicleRef, {
      control_technique_date: controlDate,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating inspection date: ', error);
    throw error;
  }
};

// Update insurance expiry
export const updateInsuranceExpiry = async (vehicleId, expiryDate) => {
  try {
    const vehicleRef = doc(db, COLLECTION_NAME, vehicleId);
    await updateDoc(vehicleRef, {
      insurance_expiry_date: expiryDate,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating insurance expiry: ', error);
    throw error;
  }
};

// Get vehicle statistics
export const getVehicleStats = async () => {
  try {
    const vehicles = await getVehicles();
    const currentDate = new Date();
    
    const stats = {
      total: vehicles.length,
      active: vehicles.filter(v => v.status === 'Active').length,
      inactive: vehicles.filter(v => v.status === 'Inactive').length,
      serviceDue: 0,
      inspectionDue: 0,
      insuranceExpiring: 0
    };

    vehicles.forEach(vehicle => {
      // Check inspection due (within 30 days)
      if (vehicle.control_technique_date) {
        const controlDate = new Date(vehicle.control_technique_date);
        const daysDiff = (controlDate - currentDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 30) {
          stats.inspectionDue++;
        }
      }

      // Check insurance expiring (within 30 days)
      if (vehicle.insurance_expiry_date) {
        const insuranceDate = new Date(vehicle.insurance_expiry_date);
        const daysDiff = (insuranceDate - currentDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 30) {
          stats.insuranceExpiring++;
        }
      }
    });

    return stats;
  } catch (error) {
    console.error('Error getting vehicle stats: ', error);
    throw error;
  }
};